// src/components/HotelBanner.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import moment from "moment";
import { format, addDays, parse } from "date-fns";
import { GET_HOTEL_SEARCH_PARAMS } from "../../utils/constants";
import { TabsNavigation } from "./TabsNavigation";
import { DestinationInput } from "./DestinationInput";
import { CitizenshipInput } from "./CitizenshipInput";
import { TravelerSelector } from "./TravelerSelector";
import { DrawerComponent } from "../common/Drawer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonIcon from "@mui/icons-material/Person";
import RoomSelector from "./RoomSelector";
import styled from 'styled-components';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { NewDatePicker } from '../common/NewDatePicker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import countriesData from "../../utils/countries-min.json";

const DatePickerWrapper = styled.div`
  position: relative;
  width: 100%;

  .calendar-icon {
    position: absolute;
    left: ${props => props.isMobile ? '4px' : '16px'};
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    color: #003663;
    pointer-events: none;
  }

  input {
    padding-left: 48px !important;
    height: 56px !important;
    width: 100% !important;
    margin-left: 0 !important;
    color: #003663 !important;
    font-size: 18px !important;
    font-weight: 400 !important;
  }

  input::placeholder {
    color: #9CA3AF !important;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: #003663;
  color: white;
  gap: 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: white;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const DrawerContent = styled.div`
  padding: 16px;
  height: calc(100vh - 64px);
  overflow-y: auto;
`;

const HotelBanner = ({ translation, translationContext }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const citizenshipRef = useRef(null);
  const roomSelectorRef = useRef(null);
  const isHotelSearchPage = location.pathname.startsWith('/hotels/search');

  // Add CSS for error placeholder and mobile menu
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .error-placeholder::placeholder {
        color: #dc3545 !important;
      }
      
      /* Ensure banner content is always above blur overlay */
      .banner {
        position: relative !important;
        z-index: 1000 !important;
      }
      
      /* Ensure banner form content is above everything */
      .banner .tab-content,
      .banner .tab-pane {
        position: relative !important;
        z-index: 1001 !important;
      }
      
      /* Ensure dropdowns are above blur */
      .list-group.position-absolute,
      .react-datepicker__tab-content,
      [role="dialog"] {
        z-index: 9999 !important;
      }
      
      /* Mobile logo and hamburger positioning (improved) */
      @media only screen and (max-width: 767px) {
        .custom-logo-override {
        }
        .navbar-brand {
          margin-top: -10px !important;
          margin-bottom: 0px !important;
        }
        .navbar-toggler {
          position: absolute !important;
          top: 50px !important;
          right: 5px !important;
          z-index: 1005 !important;
          margin-top: 0 !important;
          margin-right: 0 !important;
        }
        .hotel-banner-mobile-container input.form-control,
        .hotel-banner-mobile-container .form-control.hotel-banner-mobile-input,
        .hotel-banner-mobile-input {
          width: 100% !important;
          max-width: 100% !important;
        }
        .banner .nav-tabs {
          margin-top: 68px !important; /* bring back a bit more space under logo */
        }
      }
      
      /* Mobile container fixes */
      @media only screen and (max-width: 767px) {
        .container {
          max-width: 100% !important;
          padding-left: 0px !important;
          padding-right: 0px !important;
        }
        
        .tab-content {
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow-x: hidden !important;
        }
        
        .row {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        
        /* Ensure form inputs are properly sized and centered */
        .form-control,
        input,
        select {
          width: 100% !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
          border-radius: 8px !important;
          font-size: 18px !important;
          font-weight: 500 !important;
        }
        
        /* Fix any potential flex issues */
        .d-flex, .flex {
          flex-wrap: wrap !important;
        }
        
        /* Hotel banner mobile container, inputs and button */
        .hotel-banner-mobile-container,
        .hotel-banner-mobile-input,
        .hotel-banner-mobile-button {
          width: 100% !important;          /* take full available width */
          max-width: 100% !important;    /* allow full width */
          box-sizing: border-box !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        /* rounded corners */
        .hotel-banner-mobile-container { border-radius: 8px !important; overflow: visible !important; }
        .hotel-banner-mobile-input    { border-radius: 8px !important; }
        .hotel-banner-mobile-button   { border-radius: 16px !important; }
        
        /* Keep container centered and within viewport on very small widths */
        @media only screen and (max-width: 415px) {
          .hotel-banner-mobile-container {
            width: 100% !important;
            max-width: 420px !important;
            margin: 0 auto !important;
            padding: 0 !important;
          }
        }

        .hotel-banner-mobile-container > div {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
      }
      
      /* NewDatePicker positioning adjustment for hotel banner */
      .banner .date-picker-input + div > div[style*="position: absolute"] {
        transform: translateX(calc(-50% - 45px)) !important;
      }
      
      /* Mobile menu styles */
      .mobile-menu {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background-color: rgba(0, 54, 99, 0.95);
        z-index: 9999;
        display: flex;
        flex-direction: column;
        padding: 20px;
        transform: translateX(-100%);
        transition: transform 0.3s ease-in-out;
      }
      
      .mobile-menu.open {
        transform: translateX(0);
      }
      
      .mobile-menu-item {
        color: white;
        text-decoration: none;
        padding: 15px 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        font-size: 18px;
        font-weight: 500;
      }
      
      .mobile-menu-close {
        align-self: flex-end;
        background: none;
        border: none;
        color: white;
        font-size: 24px;
        cursor: pointer;
        margin-bottom: 20px;
      }

      @media only screen and (min-width: 768px) {
        /* Hide the header logo on hotel pages because we already render a larger one in the banner */
        .header .navbar-brand {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // All state declarations first
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentDrawer, setCurrentDrawer] = useState(null);
  const [selectLanguage, setSelectLanguage] = useState(false);
  const [currentLang, setCurrentLang] = useState(translationContext?.i18n?.language || 'en');
  const [openMenu, setOpenMenu] = useState(false);

  // Replace shouldOpenRoomSelector with direct room selector control
  const [shouldFocusCitizenship, setShouldFocusCitizenship] = useState(false);
  const [forceRoomSelectorOpen, setForceRoomSelectorOpen] = useState(false);

  // Get initial values from URL or location state
  const getInitialValues = () => {
    // First try to get from URL search params
    let cityId = searchParams.get("cityId");
    let city = searchParams.get("city");
    let checkInDate = searchParams.get("checkInDate");
    let checkOutDate = searchParams.get("checkOutDate");
    let adults = parseInt(searchParams.get("adults") || "2", 10);
    let children = parseInt(searchParams.get("children") || "0", 10);
    let nationality = searchParams.get("nationality") || "";
    let rooms = parseInt(searchParams.get("rooms") || "1", 10);
    let cityLat = searchParams.get("cityLat");
    let cityLng = searchParams.get("cityLng");

    // If we're on hotel details page and have location state, use those values instead
    if (location.pathname.includes('/hotels/details/') && location.state) {
      const state = location.state;
      
      // Use dates from navigation state if available
      if (state.checkInDate) checkInDate = state.checkInDate;
      if (state.checkOutDate) checkOutDate = state.checkOutDate;
      
      // Use other search parameters from URL params if available
      if (state.urlParams) {
        const urlParams = new URLSearchParams(state.urlParams);
        cityId = urlParams.get("cityId") || cityId;
        city = urlParams.get("city") || city;
        adults = parseInt(urlParams.get("adults") || adults, 10);
        children = parseInt(urlParams.get("children") || children, 10);
        nationality = urlParams.get("nationality") || nationality;
        rooms = parseInt(urlParams.get("rooms") || rooms, 10);
        cityLat = urlParams.get("cityLat") || cityLat;
        cityLng = urlParams.get("cityLng") || cityLng;
      }
    } else {
    }

    // Find the country name for the nationality code
    const country = nationality ? countriesData.find(c => c.cca2 === nationality) : null;
    const countryName = country ? country.name.common : "";

    // Calculate initial room display
    const initialRoomDisplay = {
      rooms: rooms,
      totalPersons: adults + children,
      adults: adults,
      children: children
    };

    return {
      cityId,
      city,
      checkInDate,
      checkOutDate,
      adults,
      children,
      nationality,
      countryName,
      rooms,
      cityLat,
      cityLng,
      initialRoomDisplay,
      initialDestination: city && cityId ? {
        name: city,
        cityId: cityId,
        latitude: cityLat,
        longitude: cityLng
      } : null,
      initialDateRange: checkInDate && checkOutDate ? {
        startDate: new Date(checkInDate),
        endDate: new Date(checkOutDate),
        key: "selection"
      } : {
        startDate: new Date(),
        endDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
        key: "selection"
      },
      initialRooms: Array(rooms).fill(null).map((_, index) => ({
        adult: index === 0 ? adults : 2,
        childAges: index === 0 ? Array(children).fill(5) : []
      }))
    };
  };

  const initialValues = getInitialValues();

  // Rest of state declarations
  const [destinationValue, setDestinationValue] = useState(initialValues.city || "");
  const [destinationSelected, setDestinationSelected] = useState(initialValues.initialDestination);
  const [cityId, setCityId] = useState(initialValues.cityId);
  const [citizenshipValue, setCitizenshipValue] = useState(initialValues.countryName || "");
  const [citizenshipCode, setCitizenshipCode] = useState(initialValues.nationality);
  const [cityLat, setCityLat] = useState(initialValues.cityLat || "");
  const [cityLng, setCityLng] = useState(initialValues.cityLng || "");
  const [roomCriteria, setRoomCriteria] = useState(initialValues.initialRooms);
  const [dateRange, setDateRange] = useState(initialValues.initialDateRange);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);

  // Update the blurBackground state to be properly used
  const [blurBackground, setBlurBackground] = useState(false);

  // Add this near the other state declarations
  const [roomDisplay, setRoomDisplay] = useState(initialValues.initialRoomDisplay);

  // Add validation states
  const [validationErrors, setValidationErrors] = useState({
    destination: false,
    citizenship: false,
    dates: false
  });

  // Add states to track when inputs are focused/open for blur effect
  const [isDestinationDropdownOpen, setIsDestinationDropdownOpen] = useState(false);
  const [isCitizenshipDropdownOpen, setIsCitizenshipDropdownOpen] = useState(false);
  const [isRoomSelectorDropdownOpen, setIsRoomSelectorDropdownOpen] = useState(false);

  // Base function declarations
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setCurrentDrawer(null);
    setBlurBackground(false); // Clear blur when drawer closes
    // Also clear individual dropdown states
    setIsDestinationDropdownOpen(false);
    setIsCitizenshipDropdownOpen(false);
    setIsCalendarOpen(false);
    setIsRoomSelectorDropdownOpen(false);
  }, []);

  const openDrawer = useCallback((type) => {
    setCurrentDrawer(type);
    setDrawerOpen(true);
    setBlurBackground(true); // Set blur when drawer opens
  }, []);

  const handleSearchClick = useCallback(() => {
    const errors = {
      destination: !cityId || !destinationSelected,
      citizenship: !citizenshipCode,
      dates: !dateRange?.startDate || !dateRange?.endDate
    };

    setValidationErrors(errors);

    if (Object.values(errors).some(error => error)) {
      return;
    }

    const totalAdults = roomCriteria.reduce((sum, room) => sum + room.adult, 0);
    const totalChildren = roomCriteria.reduce((sum, room) => sum + (room.childAges?.length || 0), 0);
    
    sessionStorage.setItem("roomCriteria", JSON.stringify(roomCriteria));
    
    const checkIn = dateRange?.startDate
      ? format(new Date(dateRange.startDate), "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd");
    const checkOut = dateRange?.endDate
      ? format(new Date(dateRange.endDate), "yyyy-MM-dd")
      : format(addDays(new Date(), 1), "yyyy-MM-dd");

    const params = new URLSearchParams();
    params.append("cityId", cityId);
    params.append("checkInDate", checkIn);
    params.append("checkOutDate", checkOut);
    params.append("adults", totalAdults);
    params.append("children", totalChildren);
    params.append("currency", "EUR");
    params.append("nationality", citizenshipCode || "");
    params.append("cityLat", cityLat);
    params.append("cityLng", cityLng);
    params.append("rooms", roomCriteria.length);
    
    // Use originalName for API calls if available (Albanian mappings), otherwise use display name
    const cityNameForApi = destinationSelected?.originalName || destinationSelected?.name || "";
    const cityNameForDisplay = destinationSelected?.name || "";
    
    
    params.append("city", cityNameForDisplay); // Use display name in URL for user reference
    params.append("apiCityName", cityNameForApi); // Include API name for backend processing

    navigate(`/hotels/search?${params.toString()}`);
  }, [
    roomCriteria,
    destinationSelected,
    cityId,
    dateRange,
    citizenshipCode,
    cityLat,
    cityLng,
    navigate
  ]);

  // Dependent function declarations
  const handleDateSelect = useCallback((dates) => {
    if (dates.startDate && dates.endDate) {
      setDateRange(dates);
      // Only open rooms drawer if both dates are selected naturally
      // (not through Done button)
      if (!isMobile) {
        handleDrawerClose();
        setTimeout(() => openDrawer('rooms'), 100);
      }
    }
  }, [handleDrawerClose, openDrawer, isMobile]);

  const handleDateChange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
    
    // If both dates are selected
    if (newDateRange?.startDate && newDateRange?.endDate) {
      if (!isMobile) {
        // ✅ DESKTOP ONLY: Close calendar and open room selector
        setIsCalendarOpen(false);
        setBlurBackground(false);
        setTimeout(() => {
          setForceRoomSelectorOpen(true);
          setIsRoomSelectorDropdownOpen(true);
          setBlurBackground(true); // Set blur when room selector opens
        }, 100);
      }
      // ✅ MOBILE: No auto-advancement - wait for DONE button click
    }
  }, [isMobile]);

  // ✅ NEW: Handle mobile DONE button click for step advancement
  const handleMobileDateDone = useCallback((dateRange) => {
    if (isMobile && dateRange?.startDate && dateRange?.endDate) {
      // Mobile: Close date drawer; open next only if not on search page
      handleDrawerClose();
      if (!isHotelSearchPage) {
        setTimeout(() => {
          openDrawer('rooms');
        }, 300);
      }
    }
  }, [isMobile, handleDrawerClose, openDrawer, isHotelSearchPage]);

  const handleDestinationSelect = useCallback((selected) => {
    if (selected) {
      setDestinationSelected(selected);
      setCityId(selected.cityId);
      setCityLat(selected.latitude);
      setCityLng(selected.longitude);
      
      // Close drawer; open next only if not on search page
      handleDrawerClose();
      if (!isHotelSearchPage) {
        // Ensure citizenship drawer opens consistently
        setTimeout(() => {
          const citizenshipInput = document.getElementById('citizenship-input');
          if (citizenshipInput) {
            setShouldFocusCitizenship(true);
          }
          openDrawer('citizenship');
        }, 300);
      }
    }
  }, [handleDrawerClose, openDrawer, isHotelSearchPage]);

  const handleCitizenshipSelect = useCallback((code, value) => {
    setCitizenshipCode(code);
    setCitizenshipValue(value);
    
    if (isMobile) {
      // Close drawer; open next only if not on search page
      handleDrawerClose();
      if (!isHotelSearchPage) {
        setTimeout(() => {
          openDrawer('dates');
        }, 300);
      }
    } else {
      // Desktop behavior - just close suggestions and open calendar
      setIsCitizenshipDropdownOpen(false);
      setBlurBackground(false);
      setTimeout(() => {
        setIsCalendarOpen(true);
        setBlurBackground(true); // Set blur when calendar opens
      }, 100);
    }
  }, [isMobile, handleDrawerClose, openDrawer, isHotelSearchPage]);

  const handleRoomSelect = useCallback((newRooms) => {
    
    // Reset the auto-open state if it was triggered
    setForceRoomSelectorOpen(false);
    
    // Calculate totals
    const totalAdults = newRooms.reduce((sum, room) => sum + room.adult, 0);
    const totalChildren = newRooms.reduce((sum, room) => sum + (room.childAges?.length || 0), 0);
    
    // Update room display state
    const newRoomDisplay = {
      rooms: newRooms.length,
      totalPersons: totalAdults + totalChildren,
      adults: totalAdults,
      children: totalChildren
    };
    
    setRoomDisplay(newRoomDisplay);
    setRoomCriteria(newRooms);
    
    // Only navigate if we're in the search results page
    if (location.pathname === '/hotels/search') {
      handleSearchClick();
    }
  }, [handleSearchClick, location.pathname]);

  // Add a separate handler for the Done button
  const handleRoomSelectDone = useCallback((newRooms) => {
    // First update the room state
    handleRoomSelect(newRooms);
    
    // Reset the auto-open state
    setForceRoomSelectorOpen(false);
    
    // Then close UI elements
    if (isMobile) {
      handleDrawerClose();
    } else {
      setIsCalendarOpen(false);
      setIsRoomSelectorDropdownOpen(false);
      setBlurBackground(false); // Clear blur when room selector closes
    }
  }, [handleRoomSelect, handleDrawerClose, isMobile]);

  // Add a function to format the room display text
  const getRoomDisplayText = useCallback((display) => {
    const text = `${display.rooms} Room | ${display.totalPersons} Person${display.totalPersons > 1 ? 's' : ''}${
      display.children > 0 ? ` (${display.adults} Adult${display.adults > 1 ? 's' : ''}, ${display.children} Child${display.children > 1 ? 'ren' : ''})` : ''
    }`;
    return text;
  }, []);

  // Effects
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Update currentLang when translationContext changes
    setCurrentLang(translationContext?.i18n?.language || 'en');
  }, [translationContext]);

  // Re-initialize when location state changes (for hotel details page)
  useEffect(() => {
    if (location.pathname.includes('/hotels/details/') && location.state) {
      const newInitialValues = getInitialValues();
      
      
      // Update date range if we have new dates from navigation state
      if (newInitialValues.initialDateRange) {
        setDateRange(newInitialValues.initialDateRange);
      }
      
      // Update other values as needed
      if (newInitialValues.city && newInitialValues.cityId) {
        setDestinationValue(newInitialValues.city);
        setCityId(newInitialValues.cityId);
        setDestinationSelected({
          name: newInitialValues.city,
          cityId: newInitialValues.cityId,
          latitude: newInitialValues.cityLat,
          longitude: newInitialValues.cityLng
        });
      }
      
      if (newInitialValues.countryName) {
        setCitizenshipValue(newInitialValues.countryName);
        setCitizenshipCode(newInitialValues.nationality);
      }
    }
  }, [location.state, location.pathname]);

  // Re-initialize when location state changes (for hotel details page)
  useEffect(() => {
    if (location.pathname.includes('/hotels/details/') && location.state) {
      const newInitialValues = getInitialValues();
            
      // Update date range if we have new dates from navigation state
      if (newInitialValues.initialDateRange) {
        setDateRange(newInitialValues.initialDateRange);
      }
      
      // Update other values as needed
      if (newInitialValues.city && newInitialValues.cityId) {
        setDestinationValue(newInitialValues.city);
        setCityId(newInitialValues.cityId);
        setDestinationSelected({
          name: newInitialValues.city,
          cityId: newInitialValues.cityId,
          latitude: newInitialValues.cityLat,
          longitude: newInitialValues.cityLng
        });
      }
      
      if (newInitialValues.countryName) {
        setCitizenshipValue(newInitialValues.countryName);
        setCitizenshipCode(newInitialValues.nationality);
      }
    }
  }, [location.state, location.pathname]);

  // Re-initialize when location state changes (for hotel details page)
  useEffect(() => {
    if (location.pathname.includes('/hotels/details/') && location.state) {
      const state = location.state;
      
      // Update dates from navigation state if available
      if (state.checkInDate && state.checkOutDate) {
        
        setDateRange({
          startDate: new Date(state.checkInDate),
          endDate: new Date(state.checkOutDate),
          key: "selection"
        });
      }
    }
  }, [location.state, location.pathname]);

  // Only update selectedDestination if we have new data
  useEffect(() => {
    if (cityId && destinationSelected && !selectedDestination) {
      const newDestination = {
        ...destinationSelected,
        cityId: cityId
      };
      // Check if the destination actually changed to prevent unnecessary updates
      if (JSON.stringify(newDestination) !== JSON.stringify(selectedDestination)) {
        setSelectedDestination(newDestination);
      }
    }
  }, [cityId, destinationSelected, selectedDestination]);

  // Remove any unnecessary useEffects
  const [step, setStep] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const openStep = useCallback((n) => {
    if (isMobile) {
      setStep(n);
      setShowModal(true);
    }
  }, [isMobile]);
  
  const closeDrawer = useCallback(() => {
    setShowModal(false);
    setStep(0);
  }, []);

  const [travelersData, setTravelersData] = useState({ citizenship: { code: "AL" } });

  const getChildrenAges = () => {
    return roomCriteria.map(room => room.childAges || []);
  };

  const [checkInDate, setCheckInDate] = useState(new Date());
  const [checkOutDate, setCheckOutDate] = useState(new Date(Date.now() + 2 * 24 * 60 * 60 * 1000));

  const baseBtn = {
    backgroundColor: "#FBD316",
    color: "#003663",
    fontWeight: "bold",
    padding: "16px 32px",
    border: "none",
    borderRadius: "16px",
    fontSize: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease-in-out",
    boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
    gap: "8px",
    width: "100%",
    cursor: "pointer",
    height: "60px",
  };
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const getDynamicButtonStyle = () => ({
    ...baseBtn,
    transform: isHovered
      ? isActive
        ? "scale(0.98)"
        : "scale(1.04)"
      : "scale(1)",
    backgroundColor: isActive
      ? "#d4aa0d"
      : isHovered
      ? "#e6be14"
      : "#FBD316",
  });

  const topbarStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    transform: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    zIndex: 9999,
    background: 'transparent',
    pointerEvents: 'auto',
    width: '100%',
    maxWidth: '100%',
    paddingLeft: '64px',
    justifyContent: 'space-between',
    minHeight: '160px'
  };

  const profileIconStyle = {
    color: 'white',
    cursor: 'pointer',
    fontSize: '24px'
  };

  const containerStyle = {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: isMobile ? "0" : "0 32px",
    position: "relative",
    width: "100%",
    paddingTop: isMobile ? "0px" : "32px",
    boxSizing: "border-box"
  };

  const sectionStyle = {
    background: isMobile 
      ? '#034C88'
      : `url(${require("../../assets/img/bg-banner-location.png")}) center center no-repeat`,
    backgroundSize: "cover",
    padding: isMobile ? "0px 0 40px" : "140px 0 0",
    marginTop: "-32px",
    position: 'relative',
    width: "100%",
    maxWidth: "100%",
    overflow: "visible"
  };

  const renderDrawerContent = useCallback(() => {
    const drawerContentStyle = isMobile ? {
      width: '100vw',
      height: '100vh',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column'
    } : {};

    const headerStyle = {
      background: '#003663',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      color: 'white'
    };

    const contentStyle = {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '0' : '24px'
    };

    const datePickerContentStyle = {
      ...contentStyle,
      padding: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    };

    const roomSelectorContentStyle = {
      ...contentStyle,
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    };

    const doneButtonStyle = {
      width: '100%',
      padding: '16px',
      backgroundColor: '#FBD316',
      color: '#003663',
      border: 'none',
      borderRadius: '0',
      fontWeight: '600',
      fontSize: '16px',
      cursor: 'pointer',
      position: 'sticky',
      bottom: 0,
      left: 0
    };

    switch (currentDrawer) {
      case 'destination':
        return (
          <div style={drawerContentStyle}>
            <div style={headerStyle}>
              <BackButton onClick={handleDrawerClose}>
                <ChevronLeftIcon />
              </BackButton>
              <DrawerTitle>{translation("destination")}</DrawerTitle>
            </div>
            <div style={contentStyle}>
              <DestinationInput
                translation={translation}
                destinationValue={destinationValue}
                destinationSelectedValue={destinationSelected}
                setDestinationValue={setDestinationValue}
                setDestinationSelectedValue={(val) => {
                  setDestinationSelected(val);
                  if (val) {
                    setSelectedDestination(val);
                    setShouldFocusCitizenship(true);
                  }
                }}
                setCityId={setCityId}
                setCityLat={setCityLat}
                setCityLng={setCityLng}
                readOnly={false}
                hasError={validationErrors.destination}
                onDropdownToggle={(isOpen) => {
                  setIsDestinationDropdownOpen(isOpen);
                }}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        );

      case 'citizenship':
        return (
          <div style={drawerContentStyle}>
            <div style={headerStyle}>
              <BackButton onClick={handleDrawerClose}>
                <ChevronLeftIcon />
              </BackButton>
              <DrawerTitle>{translation("Nationality")}</DrawerTitle>
            </div>
            <div style={contentStyle}>
              <CitizenshipInput
                ref={citizenshipRef}
                translation={translation}
                citizenshipValue={citizenshipValue}
                setCitizenshipValue={setCitizenshipValue}
                setCitizenshipCode={handleCitizenshipSelect}
                readOnly={false}
                autoFocus
                hasError={validationErrors.citizenship}
                onDropdownToggle={(isOpen) => {
                  setIsCitizenshipDropdownOpen(isOpen);
                }}
              />
            </div>
          </div>
        );

      case 'dates':
        return (
          <div style={drawerContentStyle}>
            <div style={headerStyle}>
              <BackButton onClick={handleDrawerClose}>
                <ChevronLeftIcon />
              </BackButton>
              <DrawerTitle>{translation("select_dates")}</DrawerTitle>
            </div>
            <div style={datePickerContentStyle}>
              <DrawerComponent
                open={drawerOpen}
                setOpen={setDrawerOpen}
                onClose={handleDrawerClose}
                headerText={translation("select_dates")}
                anchor="bottom"
                height="100vh"
              >
                <div style={{ 
                  height: '100vh', 
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: 'white'
                }}>
              <NewDatePicker
                isRange
                value={dateRange}
                onChange={handleDateChange}
                minDate={new Date()}
                isOpen={true}
                preventDirectOpen={false}
                isMobile={true}
                variant="hotel"
                hasError={validationErrors.dates}
              />
                </div>
              </DrawerComponent>
            </div>
          </div>
        );

      case 'rooms':
        return (
          <div style={drawerContentStyle}>
            <div style={headerStyle}>
              <BackButton onClick={handleDrawerClose}>
                <ChevronLeftIcon />
              </BackButton>
              <DrawerTitle>{translation("rooms_and_guests")}</DrawerTitle>
            </div>
            <div style={contentStyle}>
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                alignItems: 'flex-start',
                width: '100%'
              }}>
                <div style={{ flex: isMobile ? 1 : "0 1 calc(100% - 240px)" }}>
                  <div className={`row align-items-stretch ${isMobile ? '' : 'pt-4 mt-2'}`}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '16px',
                      width: '100%',
                      padding: '0 8px'
                    }}>
                      {/* Origin Input */}
                      <div className="col-lg-6 ftinput col-md-6 col-12">
                      <DestinationInput
                        translation={translation}
                        destinationValue={destinationValue}
                        destinationSelectedValue={destinationSelected}
                        setDestinationValue={setDestinationValue}
                        setDestinationSelectedValue={(val) => {
                          setDestinationSelected(val);
                          if (val) {
                            setSelectedDestination(val);
                            setShouldFocusCitizenship(true);
                          }
                        }}
                        setCityId={setCityId}
                        setCityLat={setCityLat}
                        setCityLng={setCityLng}
                        readOnly={false}
                        hasError={validationErrors.destination}
                        onDropdownToggle={(isOpen) => {
                          setIsDestinationDropdownOpen(isOpen);
                        }}
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                          }}
                      />
                    </div>
                      <div className="col-lg-6 ftinput col-md-6 col-12">
                      <CitizenshipInput
                        ref={citizenshipRef}
                        translation={translation}
                        citizenshipValue={citizenshipValue}
                        setCitizenshipValue={setCitizenshipValue}
                        setCitizenshipCode={handleCitizenshipSelect}
                        readOnly={false}
                        className="citizenship-input"
                        hasError={validationErrors.citizenship}
                        onDropdownToggle={(isOpen) => {
                          setIsCitizenshipDropdownOpen(isOpen);
                        }}
                      />
                    </div>
                  </div>
                  </div>
                  <div className={`row align-items-stretch ${isMobile ? '' : 'pt-4 mt-2'}`}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '16px',
                      width: '100%',
                      padding: '0 8px'
                    }}>
                      <div className="col-lg-6 ftinput col-md-6 col-12">
                      <DatePickerWrapper isMobile={isMobile}>
                        <NewDatePicker
                          isRange
                          value={dateRange}
                          onChange={handleDateChange}
                          minDate={new Date()}
                                                          placeholder={translation("check_in_check_out")}
                          isOpen={isCalendarOpen}
                          onOpenChange={(open) => {
                            setIsCalendarOpen(open);
                          }}
                          onDropdownToggle={(isOpen) => {
                            setIsCalendarOpen(isOpen);
                          }}
                          preventDirectOpen={isMobile}
                          variant="hotel"
                          hasError={validationErrors.dates}
                        />
                      </DatePickerWrapper>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <RoomSelector
                        ref={roomSelectorRef}
                        readOnly={false}
                        initialRooms={roomCriteria}
                        onChange={handleRoomSelect}
                        autoOpen={false}
                        forceOpen={forceRoomSelectorOpen}
                        onDone={handleRoomSelectDone}
                        translation={translation}
                        onDropdownToggle={(isOpen) => {
                          setIsRoomSelectorDropdownOpen(isOpen);
                          if (!isOpen) {
                            setForceRoomSelectorOpen(false);
                          }
                        }}
                      />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ 
                  width: isMobile ? '100%' : '240px',
                  display: 'flex',
                  alignItems: isMobile ? 'center' : 'flex-end',
                  marginTop: isMobile ? '16px' : '10px',
                }}>
                  <button
                    onClick={handleSearchClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => {
                      setIsHovered(false);
                      setIsActive(false);
                    }}
                    onMouseDown={() => setIsActive(true)}
                    onMouseUp={() => setIsActive(false)}
                    style={{ 
                      ...getDynamicButtonStyle(),
                      width: "100%",
                      height: "56px",
                      fontSize: "18px",
                      borderRadius: "16px",
                      boxSizing: "border-box"
                    }}
                  >
                    {translation("search")}
                    <img
                      src={require("../../assets/img/icon-search.png")}
                      alt={translation("search")}
                      style={{ width: 22, height: 22 }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  }, [
    currentDrawer,
    translation,
    destinationValue,
    destinationSelected,
    handleDestinationSelect,
    citizenshipValue,
    handleCitizenshipSelect,
    dateRange,
    handleDateChange,
    roomCriteria,
    handleRoomSelect,
    handleDrawerClose,
    isMobile,
    getRoomDisplayText,
    validationErrors
  ]);

  const handleDateInputClick = useCallback(() => {
    if (isMobile) {
      openDrawer('dates');
    }
  }, [isMobile, openDrawer]);

  // Effect to handle citizenship focus
  useEffect(() => {
    if (shouldFocusCitizenship) {
      
      const simulateRealClick = () => {
        const container = document.getElementById('citizenship-container');
        const input = document.getElementById('citizenship-input-field');
        
        if (input && container) {
          try { 
            // First focus and click the input
            input.focus();
            input.click();
            
            // Then click the container to ensure any container-level handlers are triggered
            container.click();
            setShouldFocusCitizenship(false); // Reset the state after successful attempt
          } catch (error) {
            console.error('❌ Error dispatching events:', error);
            setShouldFocusCitizenship(false); // Reset even on error
          }
        } else {
        }
      };

      // Try immediately and after a short delay
      simulateRealClick();
      const timer = setTimeout(() => {
        simulateRealClick();
        // Reset state after final attempt
        setShouldFocusCitizenship(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [shouldFocusCitizenship]);

  // Effect to handle room selector opening
  useEffect(() => {
    if (forceRoomSelectorOpen) {
      // The RoomSelector component will handle opening via the forceOpen prop
      // We just need to ensure the blur is set
      setBlurBackground(true);
    }
  }, [forceRoomSelectorOpen]);

  const handleLanguageChange = (lang) => {
    try {
      setCurrentLang(lang);
      if (translationContext?.i18n?.changeLanguage) {
        translationContext.i18n.changeLanguage(lang);
      } else {
        localStorage.setItem('i18nextLng', lang);
      }
      setSelectLanguage(false);
      window.location.reload();
    } catch (error) {
      console.error('Error changing language:', error);
      setCurrentLang(lang);
      localStorage.setItem('i18nextLng', lang);
      setSelectLanguage(false);
      window.location.reload();
    }
  };

  const formatDateDisplay = (dateRange, translation) => {
    if (!dateRange) return translation("select_dates");
    
    try {
      if (dateRange.startDate && dateRange.endDate) {
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          return translation("select_dates");
        }
        return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;
      } else if (dateRange.startDate) {
        const startDate = new Date(dateRange.startDate);
        if (isNaN(startDate.getTime())) {
          return translation("select_dates");
        }
        return format(startDate, 'dd/MM/yyyy');
      }
      return translation("select_dates");
    } catch (error) {
      console.error('Error formatting date:', error);
      return translation("select_dates");
    }
  };

  // Effect to manage overall blur state based on individual component states
  useEffect(() => {
    // Only enable blur on desktop, not mobile
    if (!isMobile) {
      const shouldHaveBlur = isDestinationDropdownOpen || isCitizenshipDropdownOpen || isCalendarOpen || isRoomSelectorDropdownOpen;
      setBlurBackground(shouldHaveBlur);
    } else {
      // Mobile should never have blur
      setBlurBackground(false);
    }
  }, [isMobile, isDestinationDropdownOpen, isCitizenshipDropdownOpen, isCalendarOpen, isRoomSelectorDropdownOpen]);


  return (
    <section 
      className="banner" 
      style={{
        ...sectionStyle,
        position: 'relative',
        zIndex: 'auto'
      }}
    >
      <div style={containerStyle}>
        {/* Mobile Header - Logo and Hamburger Menu (improved positioning) */}
        {isMobile && (
          <div className="container" style={{ marginBottom: '-95px' }}>
            <div className="row">
              <nav className="navbar navbar-expand-lg" style={{ position: 'relative' }}>
                <a className="navbar-brand" href="/" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  position: 'relative', 
                  zIndex: 10,
                  marginTop: '10px',
                  marginBottom: '0px',
                  marginLeft: '25px'
                }}>
                  <img 
                    src={require("../../assets/img/FluturolireLogoWhite.png")} 
                    alt="Fluturolire Logo" 
                    className="custom-logo-override"
                    style={{
                      height: '35px',
                      width: '145px',
                      maxWidth: '90vw',
                      objectFit: 'contain',
                      minHeight: '140px'
                    }}
                  />
                </a>
                <button
                  className={`navbar-toggler menu-icon ${openMenu ? "active" : ""}`}
                  type="button"
                  data-target="#navbarSupportedContent"
                  aria-controls="navbarSupportedContent"
                  aria-expanded="false"
                  aria-label="Toggle navigation"
                  onClick={() => {
                    setOpenMenu(!openMenu);
                  }}
                >
                  <span className="navbar-toggler-icon"></span>
                  <span className="navbar-toggler-icon"></span>
                  <span className="navbar-toggler-icon"></span>
                </button>
              </nav>
            </div>
          </div>
        )}

        {!isMobile && (
          <div style={{ position: 'absolute', top: '-80px', left: 0, paddingLeft: '8px', zIndex: 1002 }}>
            <a href="/" style={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={require("../../assets/img/FluturolireLogoWhite.png")}
                alt="FluturoLogo"
                style={{ height: '120px', width: '440px', objectFit: 'contain' }}
              />
            </a>
          </div>
        )}

        <TabsNavigation />
        <div className="tab-content" style={{ 
          width: "100%", 
          margin: 0, 
          padding: 0,
          boxSizing: "border-box"
        }}>
          <div id="tab-2" className="tab-pane fade in show active" style={{ 
            width: "100%", 
            margin: 0, 
            padding: 0,
            boxSizing: "border-box"
          }}>
            {isMobile && (
              <div style={{
                position: 'absolute',
                left: 0,
                right: 0,
                backgroundColor: '#034C88',
                width: '100%'
              }} />
            )}
            <div
              style={{
                position: "relative",
                margin: 0,
                width: "100%",
                padding: isMobile ? "0" : "24px",
                zIndex: 1001,
                backgroundColor: "#034C88",
                ...(isMobile ? {
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: "hidden"
                } : {
                  borderRadius: "16px"
                }),
                boxSizing: "border-box",
                minHeight: "200px",
              }}
            >
              <div
              >
                {isMobile ? (
                  <div style={{ width: '100%' }}>
                    <div 
                      className="hotel-banner-mobile-container"
                      style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                        gap: '12px',
                        width: '100%',
                    }}>
                      <div style={{ width: '100%' }} onClick={() => openDrawer('destination')}>
                        <DestinationInput
                          translation={translation}
                          destinationValue={destinationValue}
                          destinationSelectedValue={destinationSelected}
                          setDestinationValue={setDestinationValue}
                          setDestinationSelectedValue={(val) => {
                            setDestinationSelected(val);
                            if (val) {
                              setSelectedDestination(val);
                              setShouldFocusCitizenship(true);
                            }
                          }}
                          setCityId={setCityId}
                          setCityLat={setCityLat}
                          setCityLng={setCityLng}
                          readOnly={true}
                          hasError={validationErrors.destination}
                          onDropdownToggle={(isOpen) => {
                            setIsDestinationDropdownOpen(isOpen);
                          }}
                        />
                      </div>
                      <div style={{ width: '100%', boxSizing: 'border-box' }} onClick={() => openDrawer('citizenship')}>
                        <CitizenshipInput
                          ref={citizenshipRef}
                          translation={translation}
                          citizenshipValue={citizenshipValue}
                          setCitizenshipValue={setCitizenshipValue}
                          setCitizenshipCode={handleCitizenshipSelect}
                          readOnly={false}
                          hasError={validationErrors.citizenship}
                          onDropdownToggle={(isOpen) => {
                            setIsCitizenshipDropdownOpen(isOpen);
                          }}
                        />
                      </div>
                      <div style={{ width: '100%', boxSizing: 'border-box' }} onClick={() => openDrawer('dates')}>
                        <div 
                          onClick={handleDateInputClick}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: "#f8f9fa",
                            borderRadius: '8px',
                            height: '56px',
                            cursor: 'pointer',
                            padding: '0 16px',
                            position: 'relative',
                            border: validationErrors.dates ? '1px solid #dc3545' : 'none',
                          }}
                        >
                          <CalendarTodayIcon style={{ 
                            color: validationErrors.dates ? '#dc3545' : '#003663', 
                            fontSize: 24, 
                            marginRight: '8px' 
                          }} />
                          <input
                            type="text"
                            readOnly
                            value={formatDateDisplay(dateRange, translation)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              width: '100%',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: validationErrors.dates ? '#dc3545' : '#003663',
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ width: '100%', boxSizing: 'border-box' }} onClick={() => openDrawer('rooms')}>
                        <RoomSelector
                          readOnly={true}
                          initialRooms={roomCriteria}
                          onChange={handleRoomSelect}
                          translation={translation}
                        />
                      </div>
                      <button
                        className="hotel-banner-mobile-button"
                        onClick={handleSearchClick}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => {
                          setIsHovered(false);
                          setIsActive(false);
                        }}
                        onMouseDown={() => setIsActive(true)}
                        onMouseUp={() => setIsActive(false)}
                        style={{ 
                          ...getDynamicButtonStyle(),
                          width: "100%",
                          height: "56px",
                          fontSize: "18px",
                          borderRadius: "16px",
                          boxSizing: "border-box"
                        }}
                      >
                                                  {translation("search")}
                        <img
                          src={require("../../assets/img/icon-search.png")}
                          alt={translation("search")}
                          style={{ width: 22, height: 22 }}
                        />
                      </button>
                    </div>

                    <DrawerComponent
                      open={drawerOpen}
                      setOpen={setDrawerOpen}
                      onClose={handleDrawerClose}
                      headerText={
                        currentDrawer === 'destination' ? translation("select_destination") :
                        currentDrawer === 'citizenship' ? translation("select_nationality") :
                        currentDrawer === 'dates' ? translation("select_dates") :
                        currentDrawer === 'rooms' ? translation("rooms_and_guests") : ''
                      }
                      anchor="bottom"
                      height="100vh"
                    >
                      {currentDrawer === 'destination' && (
                        <div style={{ 
                          height: '100vh', 
                          display: 'flex', 
                          flexDirection: 'column',
                          backgroundColor: 'white',
                          padding: '16px'
                        }}>
                          <DestinationInput
                            translation={translation}
                            destinationValue={destinationValue}
                            destinationSelectedValue={destinationSelected}
                            setDestinationValue={setDestinationValue}
                            setDestinationSelectedValue={(val) => {
                              setDestinationSelected(val);
                              if (val) {
                                setSelectedDestination(val);
                                handleDrawerClose();
                                setTimeout(() => openDrawer('citizenship'), 300);
                              }
                            }}
                            setCityId={setCityId}
                            setCityLat={setCityLat}
                            setCityLng={setCityLng}
                            readOnly={false}
                            autoFocus
                            hasError={validationErrors.destination}
                          />
                        </div>
                      )}
                      {currentDrawer === 'citizenship' && (
                        <div style={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: 'white',
                          padding: '16px',
                          paddingTop: '40px' // Extra padding at top to ensure input is visible
                        }}>
                          <CitizenshipInput
                            ref={citizenshipRef}
                            translation={translation}
                            citizenshipValue={citizenshipValue}
                            setCitizenshipValue={setCitizenshipValue}
                            setCitizenshipCode={handleCitizenshipSelect}
                            readOnly={false}
                            autoFocus
                            hasError={validationErrors.citizenship}
                          />
                        </div>
                      )}
                      {currentDrawer === 'dates' && (
                        <div style={{ 
                          height: '100vh', 
                          display: 'flex', 
                          flexDirection: 'column',
                          backgroundColor: 'white'
                        }}>
                          <NewDatePicker
                            isRange
                            value={dateRange}
                            onChange={handleDateChange}
                            onDoneClick={handleMobileDateDone}
                            minDate={new Date()}
                            isOpen={true}
                            preventDirectOpen={false}
                            isMobile={true}
                            variant="hotel"
                            hasError={validationErrors.dates}
                          />
                        </div>
                      )}
                      {currentDrawer === 'rooms' && (
                        <div style={{ 
                          height: '100vh', 
                          display: 'flex', 
                          flexDirection: 'column',
                          backgroundColor: 'white',
                          padding: '16px'
                        }}>
                          <RoomSelector
                            readOnly={false}
                            initialRooms={roomCriteria}
                            onChange={handleRoomSelect}
                            autoOpen={true}
                            onDone={handleRoomSelectDone}
                            translation={translation}
                          />
                        </div>
                      )}
                    </DrawerComponent>
                  </div>
                ) : (
                  <>
                    <div style={{ 
                      display: 'flex', 
                      gap: '16px', 
                      alignItems: 'flex-start',
                      width: '100%'
                    }}>
                      <div style={{ flex: isMobile ? 1 : "0 1 calc(100% - 220px)" }}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                          gap: isMobile ? '16px' : '16px',
                          marginBottom: "16px" 
                        }}>
                          <div>
                            <DestinationInput
                              translation={translation}
                              destinationValue={destinationValue}
                              destinationSelectedValue={destinationSelected}
                              setDestinationValue={setDestinationValue}
                              setDestinationSelectedValue={(val) => {
                                setDestinationSelected(val);
                                if (val) {
                                  setSelectedDestination(val);
                                  setShouldFocusCitizenship(true);
                                }
                              }}
                              setCityId={setCityId}
                              setCityLat={setCityLat}
                              setCityLng={setCityLng}
                              readOnly={false}
                              hasError={validationErrors.destination}
                              onDropdownToggle={(isOpen) => {
                                setIsDestinationDropdownOpen(isOpen);
                              }}
                              style={{
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                          <div>
                            <CitizenshipInput
                              ref={citizenshipRef}
                              translation={translation}
                              citizenshipValue={citizenshipValue}
                              setCitizenshipValue={setCitizenshipValue}
                              setCitizenshipCode={handleCitizenshipSelect}
                              readOnly={false}
                              className="citizenship-input"
                              hasError={validationErrors.citizenship}
                              onDropdownToggle={(isOpen) => {
                                setIsCitizenshipDropdownOpen(isOpen);
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                          gap: isMobile ? '16px' : '16px'
                        }}>
                          <div>
                            <DatePickerWrapper isMobile={isMobile}>
                              <NewDatePicker
                                isRange
                                value={dateRange}
                                onChange={handleDateChange}
                                minDate={new Date()}
                                placeholder={translation("check_in_check_out")}
                                isOpen={isCalendarOpen}
                                onOpenChange={(open) => {
                                  setIsCalendarOpen(open);
                                }}
                                onDropdownToggle={(isOpen) => {
                                  setIsCalendarOpen(isOpen);
                                }}
                                preventDirectOpen={isMobile}
                                variant="hotel"
                                hasError={validationErrors.dates}
                              />
                            </DatePickerWrapper>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <RoomSelector
                              ref={roomSelectorRef}
                              readOnly={false}
                              initialRooms={roomCriteria}
                              onChange={handleRoomSelect}
                              autoOpen={false}
                              forceOpen={forceRoomSelectorOpen}
                              onDone={handleRoomSelectDone}
                              translation={translation}
                              onDropdownToggle={(isOpen) => {
                                setIsRoomSelectorDropdownOpen(isOpen);
                                if (!isOpen) {
                                  setForceRoomSelectorOpen(false);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{ 
                        width: isMobile ? '100%' : '220px',
                        display: 'flex',
                        alignItems: 'flex-end',
                        marginTop: isMobile ? '16px' : '70px'
                      }}>
                        <button
                          onClick={handleSearchClick}
                          onMouseEnter={() => setIsHovered(true)}
                          onMouseLeave={() => {
                            setIsHovered(false);
                            setIsActive(false);
                          }}
                          onMouseDown={() => setIsActive(true)}
                          onMouseUp={() => setIsActive(false)}
                          style={{
                            ...getDynamicButtonStyle(),
                            width: "100%",
                            height: "56px",
                            fontSize: "18px",
                            borderRadius: "16px",
                            boxSizing: "border-box"
                          }}
                        >
                          {translation("search")}
                          <img
                            src={require("../../assets/img/icon-search.png")}
                            alt={translation("search")}
                            style={{ width: 22, height: 22 }}
                          />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add blur overlay when any dropdown/picker is open - DESKTOP ONLY */}
      {!isMobile && blurBackground && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(3, 76, 136, 0.8)',
            zIndex: 500,
            pointerEvents: 'none'
          }}
        ></div>
      )}

      {/* Mobile Menu - Full screen coverage */}
      {isMobile && openMenu && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'transparent',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header - Full width blue background */}
            <div style={{
              backgroundColor: '#003663',
              color: 'white',
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              <span>menu</span>
              <button
                onClick={() => setOpenMenu(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              backgroundColor: 'white',
              flex: 1,
              padding: '20px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                padding: '16px'
              }}>
                
                {/* Language Section */}
                <div 
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 0',
                    borderBottom: '1px solid #e0e0e0',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectLanguage(!selectLanguage)}
                >
                  <span style={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.87)' }}>
                    Language
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      {currentLang === 'en' ? 'EN' : currentLang === 'al' ? 'SQ' : 'DE'}
                    </span>
                    <img 
                      src={currentLang === 'en' ? require("../../assets/img/en-64.png") : 
                           currentLang === 'al' ? require("../../assets/img/al-64.png") : 
                           require("../../assets/img/germany.png")} 
                      width={24} 
                      height={18} 
                      alt={currentLang}
                      style={{ borderRadius: '2px' }}
                    />
                  </div>
                </div>

                {/* Language Selection Dropdown */}
                {selectLanguage && (
                  <div style={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    padding: '8px',
                    margin: '8px 0'
                  }}>
                    <div 
                      onClick={() => { 
                        handleLanguageChange("al"); 
                        setSelectLanguage(false);
                        setOpenMenu(false); 
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s',
                        backgroundColor: currentLang === 'al' ? '#e3f2fd' : 'transparent'
                      }}
                    >
                      <img src={require("../../assets/img/al-64.png")} width={24} height={18} alt="al" />
                      <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.87)' }}>Albanian</span>
                    </div>
                    <div 
                      onClick={() => { 
                        handleLanguageChange("en"); 
                        setSelectLanguage(false);
                        setOpenMenu(false); 
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s',
                        backgroundColor: currentLang === 'en' ? '#e3f2fd' : 'transparent'
                      }}
                    >
                      <img src={require("../../assets/img/en-64.png")} width={24} height={18} alt="en" />
                      <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.87)' }}>English</span>
                    </div>
                    <div 
                      onClick={() => { 
                        handleLanguageChange("de"); 
                        setSelectLanguage(false);
                        setOpenMenu(false); 
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s',
                        backgroundColor: currentLang === 'de' ? '#e3f2fd' : 'transparent'
                      }}
                    >
                      <img src={require("../../assets/img/germany.png")} width={24} height={18} alt="de" />
                      <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.87)' }}>German</span>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: '#e0e0e0', margin: '0' }}></div>

                {/* About Us */}
                <div style={{
                  padding: '16px 0',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <a 
                    href="/about" 
                    style={{ 
                      textDecoration: 'none', 
                      color: 'rgba(0, 0, 0, 0.87)', 
                      fontSize: '16px'
                    }}
                    onClick={() => setOpenMenu(false)}
                  >
                    About us
                  </a>
                </div>

                {/* How to */}
                <div style={{
                  padding: '16px 0',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <a 
                    href="/how-to" 
                    style={{ 
                      textDecoration: 'none', 
                      color: 'rgba(0, 0, 0, 0.87)', 
                      fontSize: '16px'
                    }}
                    onClick={() => setOpenMenu(false)}
                  >
                    How to?
                  </a>
                </div>

                {/* Contact Us */}
                <div style={{
                  padding: '16px 0',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <a 
                    href="/contact" 
                    style={{ 
                      textDecoration: 'none', 
                      color: 'rgba(0, 0, 0, 0.87)', 
                      fontSize: '16px'
                    }}
                    onClick={() => setOpenMenu(false)}
                  >
                    Contact us
                  </a>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HotelBanner
